import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { View, Text, Image, TouchableOpacity, StyleSheet, Platform } from 'react-native';
import Animated, { useSharedValue, useAnimatedStyle, withTiming } from 'react-native-reanimated';
import type { CorkBoardReply, CorkBoardAttachment } from '@/types/residents/corkBoard';
import { InlineImageGallery } from './InlineImageGallery';
import { AttachmentPreview } from './AttachmentPreview';
import { AdaptiveMenu } from './AdaptiveMenu';
import { PRIMITIVE_COLORS } from '@/constants/residents/residentColors';
import { REDESIGN_TYPOGRAPHY } from '@/constants/residents/homeConstants';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';
import { getAvatarColor } from '@/utils/residents/avatarColors';
import { formatRelativeDate } from '@/utils/common/formatting';
import { useThemeColors } from '@/hooks/residents/useThemeColors';

interface ReplyCardProps {
  reply: CorkBoardReply;
  isOP: boolean;
  onEdit?: () => void;
  onDelete?: () => void;
  onReport?: () => void;
  onImagePress?: (imageUrls: string[], index: number) => void;
  onDocumentPress?: (url: string, title: string) => void;
}

const isImage = (a: CorkBoardAttachment) => !!a.fileType && a.fileType.startsWith('image/');

export const ReplyCard: React.FC<ReplyCardProps> = memo(({
  reply,
  isOP,
  onEdit,
  onDelete,
  onReport,
  onImagePress,
  onDocumentPress,
}) => {
  const { colors, mode } = useThemeColors();
  const [showMenu, setShowMenu] = useState(false);
  const hasActions = reply.isOwnReply ? (!!onEdit || !!onDelete) : !!onReport;
  const avatarColor = useMemo(
    () => getAvatarColor(reply.creator.name, mode),
    [reply.creator.name, mode],
  );

  const openMenu = useCallback(() => setShowMenu(true), []);
  const closeMenu = useCallback(() => setShowMenu(false), []);

  const accessibilityLabel = useMemo(() => {
    const parts = [reply.creator.name];
    if (isOP) parts.push('original poster');
    const preview = reply.body.length > 100 ? `${reply.body.slice(0, 100)}...` : reply.body;
    parts.push(preview);
    return parts.join(', ');
  }, [reply, isOP]);

  const handleEdit = useCallback(() => {
    closeMenu();
    onEdit?.();
  }, [onEdit, closeMenu]);

  const handleDelete = useCallback(() => {
    closeMenu();
    setTimeout(() => onDelete?.(), 300);
  }, [onDelete, closeMenu]);

  const handleReport = useCallback(() => {
    closeMenu();
    setTimeout(() => onReport?.(), 300);
  }, [onReport, closeMenu]);

  const imageAttachments = useMemo(
    () => reply.attachments.filter(isImage),
    [reply.attachments],
  );
  const documentAttachments = useMemo(
    () => reply.attachments.filter((a) => !isImage(a)),
    [reply.attachments],
  );

  const imageUrls = useMemo(
    () => imageAttachments.map((a) => a.fileUrl),
    [imageAttachments],
  );

  // Entry animation — manual fade+slide since FadeInDown is not available on web
  const entryOpacity = useSharedValue(Platform.OS === 'web' ? 1 : 0);
  const entryTranslateY = useSharedValue(Platform.OS === 'web' ? 0 : 8);

  useEffect(() => {
    if (Platform.OS !== 'web') {
      entryOpacity.value = withTiming(1, { duration: 200 });
      entryTranslateY.value = withTiming(0, { duration: 200 });
    }
  }, [entryOpacity, entryTranslateY]);

  const entryAnimatedStyle = useAnimatedStyle(() => ({
    opacity: entryOpacity.value,
    transform: [{ translateY: entryTranslateY.value }],
  }));

  const containerStyle = useMemo(
    () => [
      styles.container,
      { backgroundColor: colors.card.bg, borderColor: colors.card.border },
      isOP && styles.containerOP,
    ],
    [isOP, colors],
  );

  const cardContent = (
    <View style={containerStyle} accessibilityLabel={accessibilityLabel}>
      {/* Header row: avatar + stacked author info + menu */}
      <View style={styles.headerRow}>
        <View style={styles.authorSection}>
          {/* Avatar */}
          {reply.creator.photoUrl ? (
            <Image
              source={{ uri: reply.creator.photoUrl }}
              style={styles.avatarImage}
              accessibilityIgnoresInvertColors
            />
          ) : (
            <View style={[styles.avatar, { backgroundColor: avatarColor.bg }]}>
              <Text style={[styles.avatarText, { color: avatarColor.text }]}>
                {reply.creator.initials}
              </Text>
            </View>
          )}

          {/* Stacked author info */}
          <View style={styles.authorInfo}>
            <View style={styles.nameRow}>
              <Text style={[styles.authorName, { color: colors.text.secondary }]} numberOfLines={1}>{reply.creator.name}</Text>
              {isOP && (
                <View style={styles.opBadge}>
                  <Text style={styles.opBadgeText}>OP</Text>
                </View>
              )}
            </View>
            <Text style={[styles.timestamp, { color: colors.text.dim }]}>{formatRelativeDate(reply.createdAt)}</Text>
          </View>
        </View>

        {/* Three-dot menu */}
        {hasActions && (
          <View style={styles.menuAnchor}>
            <TouchableOpacity
              style={styles.menuButton}
              onPress={openMenu}
              hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
              accessibilityRole="button"
              accessibilityLabel="Reply actions"
            >
              <RESIDENTS_SVG_COMPONENTS.THREE_DOTS width={16} height={16} color={colors.text.dimmer} />
            </TouchableOpacity>

            <AdaptiveMenu visible={showMenu} onClose={closeMenu}>
              <View style={styles.sheetContent}>
                {reply.isOwnReply ? (
                  <>
                    {onEdit && (
                      <TouchableOpacity style={styles.sheetItem} onPress={handleEdit} activeOpacity={0.7} accessibilityRole="button" accessibilityLabel="Edit reply">
                        <Text style={[styles.sheetItemText, { color: colors.text.primary }]}>Edit reply</Text>
                      </TouchableOpacity>
                    )}
                    {onDelete && (
                      <TouchableOpacity style={styles.sheetItem} onPress={handleDelete} activeOpacity={0.7} accessibilityRole="button" accessibilityLabel="Delete reply">
                        <Text style={[styles.sheetItemTextDanger, { color: colors.accent.red }]}>Delete reply</Text>
                      </TouchableOpacity>
                    )}
                  </>
                ) : (
                  onReport && (
                    <TouchableOpacity style={styles.sheetItem} onPress={handleReport} activeOpacity={0.7} accessibilityRole="button" accessibilityLabel="Report reply">
                      <Text style={[styles.sheetItemTextDanger, { color: colors.accent.red }]}>Report reply</Text>
                    </TouchableOpacity>
                  )
                )}
              </View>
            </AdaptiveMenu>
          </View>
        )}
      </View>

      {/* Body text */}
      <Text style={[styles.body, { color: colors.text.primary }]}>{reply.body}</Text>

      {/* Inline image gallery */}
      {imageAttachments.length > 0 && onImagePress && (
        <InlineImageGallery
          imageUrls={imageUrls}
          onImagePress={onImagePress}
        />
      )}

      {/* Document attachments */}
      {documentAttachments.length > 0 && (
        <AttachmentPreview
          attachments={documentAttachments}
          onImagePress={onImagePress}
          onDocumentPress={onDocumentPress}
        />
      )}
    </View>
  );

  return (
    <Animated.View style={entryAnimatedStyle}>
      {cardContent}
    </Animated.View>
  );
});

ReplyCard.displayName = 'ReplyCard';

const styles = StyleSheet.create({
  container: {
    borderRadius: 16,
    borderWidth: 1,
    padding: 16,
    gap: 10,
    overflow: 'visible',
    zIndex: 1,
  },
  containerOP: {
    borderLeftWidth: 3,
    borderLeftColor: PRIMITIVE_COLORS.resioBlue,
  },
  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    overflow: 'visible',
    zIndex: 10,
  },
  authorSection: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    flex: 1,
    minWidth: 0,
  },
  avatar: {
    width: 32,
    height: 32,
    borderRadius: 16,
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 2,
    borderColor: 'rgba(255,255,255,0.10)',
  },
  avatarImage: {
    width: 32,
    height: 32,
    borderRadius: 16,
    borderWidth: 2,
    borderColor: 'rgba(255,255,255,0.10)',
  },
  avatarText: {
    ...REDESIGN_TYPOGRAPHY.label,
    letterSpacing: 0,
    textTransform: undefined,
  },
  authorInfo: {
    flex: 1,
    minWidth: 0,
    gap: 2,
  },
  nameRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  authorName: {
    ...REDESIGN_TYPOGRAPHY.authorName,
    flexShrink: 1,
  },
  opBadge: {
    backgroundColor: 'rgba(70,114,254,0.12)',
    borderRadius: 4,
    paddingHorizontal: 6,
    paddingVertical: 1,
  },
  opBadgeText: {
    ...REDESIGN_TYPOGRAPHY.label,
    fontSize: 10,
    fontWeight: '700',
    color: PRIMITIVE_COLORS.resioBlue,
  },
  timestamp: {
    ...REDESIGN_TYPOGRAPHY.caption,
  },
  menuAnchor: {
    position: 'relative',
  },
  menuButton: {
    width: 28,
    height: 28,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
  sheetContent: {
    gap: 4,
  },
  sheetItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    paddingVertical: 14,
    paddingHorizontal: 4,
    borderRadius: 8,
  },
  sheetItemText: {
    ...REDESIGN_TYPOGRAPHY.body,
    fontSize: 16,
  },
  sheetItemTextDanger: {
    ...REDESIGN_TYPOGRAPHY.body,
    fontSize: 16,
  },
  body: {
    ...REDESIGN_TYPOGRAPHY.body,
    lineHeight: 21,
  },
});
